import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormI18n,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns the EntityForm configuration for Workflow Definition create/edit forms.
 *
 * @param mode - Whether the form is for creating or editing a workflow definition.
 * @param workflowSlug - The workflow slug (required in edit mode for fetching data).
 */
export function getWorkflowDefinitionFormConfig(
  mode: 'create' | 'edit',
  workflowSlug?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';

  return {
    id: isEdit
      ? createFormEditId('workflow-definition', workflowSlug)
      : createFormCreateId('workflow-definition'),

    endpoint: createFormEndpoint(
      isEdit,
      '/api/admin/workflows',
      `/api/admin/workflows/${workflowSlug}`,
      !!workflowSlug,
      ['slug', 'name', 'description', 'is_active', 'version', 'parent_definition_id'],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. claim-filing',
        helperText: 'Unique identifier (lowercase letters, numbers, and hyphens only)',
        order: 1,
        section: 'basic',
        disabled: isEdit,
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be at most 100 characters' },
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Claim Filing Workflow',
        order: 2,
        section: 'basic',
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be at most 255 characters' },
        },
      },
      description: {
        type: 'textarea',
        label: 'Description',
        placeholder: 'Optional description of the workflow',
        order: 3,
        section: 'basic',
        validation: {
          max: { value: 1000, message: 'Description must be at most 1000 characters' },
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        helperText: 'Enable or disable this workflow definition',
        order: 4,
        section: 'basic',
      },
      version: {
        type: 'number',
        label: 'Version',
        placeholder: 'e.g. 1',
        helperText: 'Version number for this definition',
        order: 5,
        section: 'advanced',
      },
      parent_definition_id: {
        type: 'text',
        label: 'Parent Definition ID',
        placeholder: 'UUID of parent workflow definition',
        helperText: 'Optional parent workflow for inheritance',
        order: 6,
        section: 'advanced',
      },
      config: {
        type: 'textarea',
        label: 'Config (JSON)',
        placeholder: '{}',
        helperText: 'JSON object with additional workflow configuration',
        order: 7,
        section: 'advanced',
        validation: {
          max: { value: 10000, message: 'Config must be at most 10000 characters' },
        },
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        order: 1,
      },
      {
        id: 'advanced',
        title: 'Advanced',
        order: 2,
      },
    ],

    actions: createFormActions('Workflow', isEdit, { alignment: 'right' }),

    layout: createFormLayout(),

    workflow: createFormWorkflow(),

    i18n: createFormI18n('Workflow', isEdit),
  };
}
